/**
 * MaterialReview.jsx - Material Periodic Review module
 * Review and edit material/plant master data with role-based access
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { materialReviewAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useUser } from '../contexts/UserContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableDropdown from '../components/SearchableDropdown';
import EditableCell from '../components/EditableCell';
import DraggableFillCell from '../components/DraggableFillCell';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { formatNumber, formatDate, formatShortDate, getErrorMessage } from '../utils/helpers';
import { loadSelfExcludingFilterOptions } from '../utils/filterOptions';

const MaterialReview = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const { user } = useUser();

  const filterOptionKeyByFilter = {
    plant: 'plants',
    material_combo: 'material_combos',
    family: 'families',
    mrp_controller: 'mrp_controllers',
    assigned_to_planner_name: 'assigned_to_planner_names',
    mrp_type: 'mrp_types',
    purchasing_group: 'purchasing_groups',
    procurement_type: 'procurement_types',
    strategy_group: 'strategy_groups',
    special_procurement_key: 'special_procurement_keys',
    material_type: 'material_types',
    plant_category: 'plant_categories',
    record_review_due_date: 'record_review_due_dates',
  };
  
  // Format decimal values: whole numbers without decimals, decimals to 2 places
  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    // Handle zero explicitly
    if (num === 0) return '0';
    // Convert to string - only remove trailing zeros after decimal point
    const str = num.toString();
    // Only apply regex if there's actually a decimal point
    if (str.includes('.')) {
      return str.replace(/\.?0+$/, ''); // e.g., 1.500 -> 1.5, 1.0 -> 1
    }
    return str; // Return whole numbers unchanged (e.g., 100 stays 100)
  };
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [lookupTables, setLookupTables] = useState({});
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  
  const [pendingEdits, setPendingEdits] = useState({});
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [pendingReviewChanges, setPendingReviewChanges] = useState({}); // Track items marked/unmarked for review
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState({ records: [], action: 'saved' });
  const [showReviewed, setShowReviewed] = useState(false); // Toggle for showing reviewed records
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [dragRange, setDragRange] = useState(null); // Track {start, end} indices during drag

  // AbortController ref to cancel in-flight requests
  const abortControllerRef = useRef(null);

  // Memoized data loading functions
  const loadRecords = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    setLoading(true);
    try {
      console.log('Loading records with params:', { page, pageSize, showReviewed, filters });
      const data = await materialReviewAPI.getRecords({ 
        page, 
        page_size: pageSize, 
        show_reviewed: showReviewed,
        ...filters 
      }, signal);
      console.log('Records loaded:', data.items?.length, 'Total:', data.total);
      setRecords(data.items || []);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Error loading records:', error);
        showError(getErrorMessage(error));
        setRecords([]); // Set empty array on error to prevent blank screen
        setTotalItems(0);
        setTotalPages(0);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [page, pageSize, showReviewed, filters, showError]);

  const loadFilterOptions = useCallback(async () => {
    setFilterOptionsLoading(true);
    try {
      const options = await loadSelfExcludingFilterOptions({
        getFilterOptions: materialReviewAPI.getFilterOptions,
        filters,
        baseParams: { show_reviewed: showReviewed },
        filterOptionKeyByFilter,
      });
      setFilterOptions(options);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    } finally {
      setFilterOptionsLoading(false);
    }
  }, [filters, showReviewed]);

  useEffect(() => {
    loadRecords();
    loadLookupTables();
    loadFilterOptions();
    
    // Cleanup: abort any pending requests on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadRecords, loadFilterOptions, page, filters, showReviewed]);

  // Expand/Collapse all rows
  const handleExpandAll = () => {
    setExpandedRows(new Set(records.map(r => r.MaterialPlantRecordID)));
  };

  const handleCollapseAll = () => {
    setExpandedRows(new Set());
  };

  const loadLookupTables = async () => {
    try {
      const lookups = await materialReviewAPI.getLookupTables();
      setLookupTables(lookups);
    } catch (error) {
      console.error('Failed to load lookup tables:', error);
    }
  };

  // Filter Special Procurement Keys by plant (plant-specific dropdown)
  const getSpecialProcurementKeysForPlant = useCallback((plantNK) => {
    if (!lookupTables.special_procurement_keys || !plantNK) {
      return [];
    }
    return lookupTables.special_procurement_keys
      .filter(key => key.plant === plantNK)
      .map(key => ({ value: key.value, label: key.label }));
  }, [lookupTables.special_procurement_keys]);

  const handleCellEdit = (recordId, field, value) => {
    // Prevent editing when viewing reviewed records
    if (showReviewed) {
      showWarning('Cannot edit reviewed records. Unmark them first to make changes.');
      return;
    }
    
    // Handle NaN values - convert to null for backend
    const processedValue = (typeof value === 'number' && isNaN(value)) ? null : value;
    
    setPendingEdits((prev) => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        MaterialPlantRecordID: recordId,
        [field]: processedValue,
      },
    }));

    const updatedRecords = records.map((record) =>
      record.MaterialPlantRecordID === recordId ? { ...record, [field]: processedValue } : record
    );
    setRecords(updatedRecords);
  };

  // Handle Excel-like drag-to-fill operation
  const handleFillDown = (startIndex, endIndex, field, value) => {
    const newPendingEdits = { ...pendingEdits };
    const updatedRecords = [...records];
    let count = 0;
    let skippedCount = 0;

    // For Special Procurement Key, validate that records have the same plant
    const isSpecialProcKey = field === 'SpecialProcurementKey';
    const sourcePlant = isSpecialProcKey ? records[startIndex]?.Plant : null;

    // Fill from startIndex + 1 to endIndex (inclusive)
    for (let i = startIndex + 1; i <= endIndex && i < records.length; i++) {
      const recordId = records[i].MaterialPlantRecordID;
      
      // Skip if Special Procurement Key and plant doesn't match
      if (isSpecialProcKey && records[i].Plant !== sourcePlant) {
        skippedCount++;
        continue;
      }
      
      newPendingEdits[recordId] = {
        ...newPendingEdits[recordId],
        MaterialPlantRecordID: recordId,
        [field]: value,
      };
      updatedRecords[i] = { ...updatedRecords[i], [field]: value };
      count++;
    }

    setPendingEdits(newPendingEdits);
    setRecords(updatedRecords);
    setDragRange(null); // Clear drag range after fill
    
    if (count > 0) {
      showSuccess(`Filled ${field} to ${count} row${count !== 1 ? 's' : ''}`);
    }
    if (skippedCount > 0) {
      showWarning(`Skipped ${skippedCount} row${skippedCount !== 1 ? 's' : ''} with different plant (Special Procurement Key is plant-specific)`);
    }
  };

  // Update drag range for visual feedback (called from DraggableFillCell)
  const handleDragRangeChange = (startIndex, endIndex) => {
    if (startIndex !== null && endIndex !== null) {
      setDragRange({ start: startIndex, end: endIndex });
    } else {
      setDragRange(null);
    }
  };

  const handleSaveChanges = async () => {
    console.log('=== SAVE CHANGES STARTED ===');
    // Collect ALL items with pending edits (no selection needed)
    const selectedUpdates = Object.values(pendingEdits);
    
    // Collect ALL items with pending review changes (no selection needed)
    const reviewChanges = Object.entries(pendingReviewChanges)
      .map(([recordId, markAsReviewed]) => ({
        MaterialPlantRecordID: parseInt(recordId),
        markAsReviewed
      }));
    
    console.log('Pending edits:', pendingEdits);
    console.log('Pending review changes:', pendingReviewChanges);
    console.log('Updates to save:', selectedUpdates);
    console.log('Review changes to save:', reviewChanges);
    
    if (selectedUpdates.length === 0 && reviewChanges.length === 0) {
      showWarning('No changes to save for selected records');
      return;
    }

    setSaving(true);
    try {
      let totalSaved = 0;
      const allChangedRecords = [];
      let hasErrors = false;
      
      // Save field edits if any
      if (selectedUpdates.length > 0) {
        console.log('Sending bulk update:', selectedUpdates);
        try {
          const response = await materialReviewAPI.bulkUpdate(selectedUpdates);
          console.log('Bulk update response:', response);
          
          if (response.errors && response.errors.length > 0) {
            console.error('Update errors:', response.errors);
            hasErrors = true;
            if (response.success_count === 0) {
              showError(`Failed to update records. Errors: ${response.errors.join('; ')}`);
              setSaving(false);
              return;
            } else {
              showWarning(`Updated ${response.success_count} of ${response.total_count} records. Some errors occurred.`);
            }
          }
          
          totalSaved += response.success_count || 0;
          
          // Track changed records for success modal
          selectedUpdates.forEach(update => {
            const record = records.find(r => r.MaterialPlantRecordID === update.MaterialPlantRecordID);
            allChangedRecords.push({
              plant: record?.Plant,
              material: record?.Material,
              description: record?.MaterialDescription,
              changes: update
            });
          });
        } catch (error) {
          console.error('Error saving field edits:', error);
          showError(`Failed to save field edits: ${getErrorMessage(error)}`);
          hasErrors = true;
          setSaving(false);
          return;
        }
      }
      
      // Process review status changes if any
      if (reviewChanges.length > 0) {
        const toMark = reviewChanges.filter(c => c.markAsReviewed).map(c => c.MaterialPlantRecordID);
        const toUnmark = reviewChanges.filter(c => !c.markAsReviewed).map(c => c.MaterialPlantRecordID);
        
        try {
          if (toMark.length > 0) {
            console.log('Marking as reviewed:', toMark);
            await materialReviewAPI.markAsReviewed(toMark);
            totalSaved += toMark.length;
          }
          
          if (toUnmark.length > 0) {
            console.log('Unmarking as reviewed:', toUnmark);
            await materialReviewAPI.unmarkReviewed(toUnmark);
            totalSaved += toUnmark.length;
          }
          
          // Track reviewed records for success modal
          reviewChanges.forEach(change => {
            const record = records.find(r => r.MaterialPlantRecordID === change.MaterialPlantRecordID);
            // Check if this record already has field edits tracked
            const existingRecord = allChangedRecords.find(r => 
              r.material === record?.Material && r.plant === record?.Plant
            );
            
            if (existingRecord) {
              // Add review status to existing record
              existingRecord.reviewStatus = change.markAsReviewed ? 'Marked as Reviewed' : 'Unmarked';
            } else {
              // Add new record for review change only
              allChangedRecords.push({
                plant: record?.Plant,
                material: record?.Material,
                description: record?.MaterialDescription,
                reviewStatus: change.markAsReviewed ? 'Marked as Reviewed' : 'Unmarked'
              });
            }
          });
        } catch (error) {
          console.error('Error updating review status:', error);
          showError(`Failed to update review status: ${getErrorMessage(error)}`);
          hasErrors = true;
          setSaving(false);
          return;
        }
      }
      
      // Clear pending changes for selected records only if no errors
      if (!hasErrors) {
        const newPendingEdits = { ...pendingEdits };
        selectedUpdates.forEach(update => {
          delete newPendingEdits[update.MaterialPlantRecordID];
        });
        setPendingEdits(newPendingEdits);
        
        const newPendingReviews = { ...pendingReviewChanges };
        reviewChanges.forEach(change => {
          delete newPendingReviews[change.MaterialPlantRecordID];
        });
        setPendingReviewChanges(newPendingReviews);
      }
      
      setSelectedRecords(new Set());
      setSaving(false);
      
      // Start reloading records in background (don't await - let it run async)
      console.log('Starting background reload of records...');
      loadRecords().then(() => {
        console.log('Background reload complete, count:', records.length);
      }).catch((error) => {
        console.error('Background reload error:', error);
      });
      
      // Show success message immediately (modal displays while reload happens in background)
      console.log('Save complete - totalSaved:', totalSaved, 'hasErrors:', hasErrors, 'allChangedRecords:', allChangedRecords);
      if (!hasErrors) {
        // Always show modal or toast when something was saved
        if (totalSaved > 0) {
          if (allChangedRecords.length > 0) {
            console.log('Showing success modal with', allChangedRecords.length, 'records');
            setSuccessDetails({ records: allChangedRecords, action: 'saved' });
            setShowSuccessModal(true);
          } else {
            // Fallback toast if allChangedRecords is empty for some reason
            console.log('Showing toast notification - allChangedRecords was empty');
            showSuccess(`Successfully saved ${totalSaved} record(s)`);
          }
        } else {
          console.warn('totalSaved is 0, not showing confirmation');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      showError(getErrorMessage(error));
      setSaving(false);
    }
  };

  const toggleReviewStatus = (recordId, currentStatus) => {
    // Prevent toggling when viewing reviewed records
    if (showReviewed) {
      showWarning('Cannot change review status when viewing reviewed records. Use the "Show Reviewed Records" toggle to switch views.');
      return;
    }
    
    // Toggle review status
    const newStatus = !currentStatus;
    
    // Find the original record to check original value
    const originalRecord = records.find(r => r.MaterialPlantRecordID === recordId);
    const originalStatus = originalRecord ? (originalRecord.IsRecordReviewed === 1 || originalRecord.IsRecordReviewed === true) : false;
    
    setPendingReviewChanges((prev) => {
      const newChanges = { ...prev };
      
      // If the new status matches the original status, remove from pending changes
      if (newStatus === originalStatus) {
        delete newChanges[recordId];
      } else {
        // Otherwise, track the change
        newChanges[recordId] = newStatus;
      }
      
      return newChanges;
    });
  };

  const toggleAllReviewStatus = () => {
    // Prevent toggling when viewing reviewed records
    if (showReviewed) {
      showWarning('Cannot change review status when viewing reviewed records. Use the "Show Reviewed Records" toggle to switch views.');
      return;
    }

    // Determine if we should check or uncheck all
    // If any record is unchecked, check all. If all are checked, uncheck all.
    const allChecked = records.every(record => {
      const isReviewed = pendingReviewChanges[record.MaterialPlantRecordID] !== undefined
        ? pendingReviewChanges[record.MaterialPlantRecordID]
        : (record.IsRecordReviewed === 1 || record.IsRecordReviewed === true);
      return isReviewed;
    });

    const newStatus = !allChecked;
    const newChanges = {};
    
    // For each record, check if new status differs from original
    records.forEach(record => {
      const originalStatus = record.IsRecordReviewed === 1 || record.IsRecordReviewed === true;
      
      // Only add to pending changes if the new status differs from original
      if (newStatus !== originalStatus) {
        newChanges[record.MaterialPlantRecordID] = newStatus;
      }
      // If newStatus matches original, we don't add it (effectively removing any pending change)
    });
    
    setPendingReviewChanges(newChanges);
  };

  const handleUnmarkReviewed = async () => {
    if (selectedRecords.size === 0) {
      showWarning('No records selected');
      return;
    }

    setLoading(true);
    try {
      await materialReviewAPI.unmarkReviewed(Array.from(selectedRecords));
      
      // Capture record details before reload
      const unmarkedRecords = Array.from(selectedRecords).map(recordId => {
        const record = records.find(r => r.MaterialPlantRecordID === recordId);
        return {
          plant: record?.Plant,
          material: record?.Material,
          description: record?.MaterialDescription
        };
      });
      
      setSelectedRecords(new Set());
      await loadRecords();
      
      showSuccess(`Successfully unmarked ${unmarkedRecords.length} record(s) as not reviewed`);
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectRecord = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const handleDiscardChanges = () => {
    const editCount = Object.keys(pendingEdits).length;
    const reviewCount = Object.keys(pendingReviewChanges).length;
    const totalCount = editCount + reviewCount;
    
    if (totalCount === 0) {
      showWarning('No pending changes to discard');
      return;
    }
    
    setPendingEdits({});
    setPendingReviewChanges({});
    loadRecords(); // Reload to restore original values
    showSuccess(`Discarded ${totalCount} pending change(s)`);
  };

  const handleExport = async () => {
    try {
      showSuccess('Preparing export...');
      
      // Call export API with current filters AND show_reviewed toggle
      const blob = await materialReviewAPI.exportToExcel({ ...filters, show_reviewed: showReviewed });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Material_Review_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      showError(getErrorMessage(error));
    }
  };

  const pendingCount = Object.keys(pendingEdits || {}).length;
  const pendingReviewCount = Object.keys(pendingReviewChanges || {}).length;
  
  // Count unique records with ANY changes (edits or review status)
  const modifiedRecordIds = new Set([
    ...Object.keys(pendingEdits || {}).map(id => parseInt(id)),
    ...Object.keys(pendingReviewChanges || {}).map(id => parseInt(id))
  ]);
  const totalPendingCount = modifiedRecordIds.size;

  return (
    <div>
      <div className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Material Periodic Review</h1>
            <p className="mt-0.5 text-xs text-gray-600">
              Review and edit material/plant master data records
            </p>
          </div>
          <a
            href="https://eastmanchem.sharepoint.com/:p:/r/sites/ibpcoe/Shared%20Documents/Data%20Management/IBP%20Material%20Periodic%20Review%20QRG.pptx?d=w1f8132c382b841a5abaa4de1a1d71408&csf=1&web=1&e=FvpBrx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-eastman-blue hover:text-blue-700 text-xs sm:text-sm font-medium inline-flex items-center self-start"
          >
            Quick Reference Guide
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showReviewed}
              onChange={(e) => {
                setShowReviewed(e.target.checked);
                setPage(1);
                setSelectedRecords(new Set());
                setPendingEdits({});
              setPendingReviewChanges({});
              }}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-gray-700 whitespace-normal">
              {showReviewed ? 'Showing Reviewed Records' : 'Show Reviewed Records'}
            </span>
          </label>
          {showReviewed && (
            <span className="text-xs text-gray-500 italic">
              (Read-only view - unmark to edit)
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-panel">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Plant</label>
            <SearchableDropdown
              options={filterOptions.plants || []}
              value={filters.plant || ''}
              onChange={(value) => setFilters({ ...filters, plant: value })}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Plants'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Material</label>
            <SearchableDropdown
              options={filterOptions.material_combos || []}
              value={filters.material_combo || ''}
              onChange={(value) => setFilters({ ...filters, material_combo: value })}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Materials'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">MRP Controller</label>
            <SearchableDropdown
              options={filterOptions.mrp_controllers || []}
              value={filters.mrp_controller || ''}
              onChange={(value) => setFilters({ ...filters, mrp_controller: value })}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All MRP Controllers'}
              disabled={filterOptionsLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Assigned To</label>
            <SearchableDropdown
              options={filterOptions.assigned_to_planner_names || []}
              value={filters.assigned_to_planner_name || ''}
              onChange={(value) => setFilters({ ...filters, assigned_to_planner_name: value })}
              placeholder={filterOptionsLoading ? 'Loading...' : 'All Planners'}
              disabled={filterOptionsLoading}
            />
          </div>
        </div>

        {/* Additional Filters - Collapsible */}
        <div className="mt-3">
          <button
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            {showAdditionalFilters ? '− Hide' : '+ Show'} Additional Filters
          </button>
        </div>

        {showAdditionalFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Family</label>
              <SearchableDropdown
                options={filterOptions.families || []}
                value={filters.family || ''}
                onChange={(value) => setFilters({ ...filters, family: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Families'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Material Type</label>
              <SearchableDropdown
                options={filterOptions.material_types || []}
                value={filters.material_type || ''}
                onChange={(value) => setFilters({ ...filters, material_type: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Material Types'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Plant Category</label>
              <SearchableDropdown
                options={filterOptions.plant_categories || []}
                value={filters.plant_category || ''}
                onChange={(value) => setFilters({ ...filters, plant_category: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Plant Categories'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">MRP Type</label>
              <SearchableDropdown
                options={filterOptions.mrp_types || []}
                value={filters.mrp_type || ''}
                onChange={(value) => setFilters({ ...filters, mrp_type: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All MRP Types'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Purchasing Group</label>
              <SearchableDropdown
                options={filterOptions.purchasing_groups || []}
                value={filters.purchasing_group || ''}
                onChange={(value) => setFilters({ ...filters, purchasing_group: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Purchasing Groups'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Procurement Type</label>
              <SearchableDropdown
                options={filterOptions.procurement_types || []}
                value={filters.procurement_type || ''}
                onChange={(value) => setFilters({ ...filters, procurement_type: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Procurement Types'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Strategy Group</label>
              <SearchableDropdown
                options={filterOptions.strategy_groups || []}
                value={filters.strategy_group || ''}
                onChange={(value) => setFilters({ ...filters, strategy_group: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Strategy Groups'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Special Procurement Key</label>
              <SearchableDropdown
                options={filterOptions.special_procurement_keys || []}
                value={filters.special_procurement_key || ''}
                onChange={(value) => setFilters({ ...filters, special_procurement_key: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Procurement Keys'}
                disabled={filterOptionsLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Record Review Due Date</label>
              <SearchableDropdown
                options={filterOptions.record_review_due_dates || []}
                value={filters.record_review_due_date || ''}
                onChange={(value) => setFilters({ ...filters, record_review_due_date: value })}
                placeholder={filterOptionsLoading ? 'Loading...' : 'All Due Dates'}
                disabled={filterOptionsLoading}
              />
            </div>
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <div className="space-x-2">
            <button 
              onClick={() => {
                setFilters({});
                setPage(1);
              }} 
              className="btn btn-secondary btn-sm"
            >
              Clear Filters
            </button>
            <button 
              onClick={handleExport} 
              className="btn btn-secondary btn-sm"
            >
              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>
            {showReviewed && selectedRecords.size > 0 && (
              <button 
                onClick={handleUnmarkReviewed} 
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                Unmark {selectedRecords.size} as Reviewed
              </button>
            )}
          </div>
          <div className="space-x-2">
            {!showReviewed && (
              <>
                <button 
                  onClick={handleDiscardChanges} 
                  className="btn btn-secondary btn-sm" 
                  disabled={totalPendingCount === 0 || saving}
                >
                  Discard {totalPendingCount} Change{totalPendingCount !== 1 ? 's' : ''}
                </button>
                <button 
                  onClick={handleSaveChanges} 
                  className="btn btn-primary btn-sm" 
                  disabled={totalPendingCount === 0 || saving}
                >
                  {saving ? 'Saving...' : `Save ${totalPendingCount} Change${totalPendingCount !== 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading records...</p>
        </div>
      ) : records.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-lg px-6">
          {showReviewed ? (
            <>
              <p className="text-gray-700 text-lg font-medium text-center max-w-4xl">
                You have no recently reviewed material plant items.
              </p>
              <p className="text-gray-600 text-sm mt-3 text-center max-w-4xl">
                To make manual adjustments, use SAP transaction MM03 and edit the MRP tabs directly.
                If you have received this message in error, contact IBP Data Management.
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-700 text-lg font-medium text-center max-w-4xl">
                You do not have any material plant reviews due at this time.
              </p>
              <p className="text-gray-600 text-sm mt-3 text-center max-w-4xl">
                Reviews are assigned via MRP controller assignments and will show here if review is due in the next 6 months.
                If you believe you have received this message in error, please contact your COE and IBP Data Management.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="table-container overflow-x-auto">
            <table className="data-table">
              <thead>
                {/* Column Group Headers Row */}
                <tr className="bg-gray-50">
                  {showReviewed && (
                    <th rowSpan="2" style={{ width: '40px' }} className="!text-sm font-semibold text-center">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRecords(new Set(records.map(r => r.MaterialPlantRecordID)));
                          } else {
                            setSelectedRecords(new Set());
                          }
                        }}
                        checked={records.length > 0 && selectedRecords.size === records.length}
                        title="Select/Deselect all"
                        className="cursor-pointer"
                      />
                    </th>
                  )}
                  <th rowSpan="2" style={{ width: '30px' }}>
                    <button
                      onClick={expandedRows.size === records.length ? handleCollapseAll : handleExpandAll}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      title={expandedRows.size === records.length ? 'Collapse All' : 'Expand All'}
                    >
                      {expandedRows.size === records.length ? '−' : '+'}
                    </button>
                  </th>
                  <th rowSpan="2" style={{ width: '70px' }} className="!text-sm font-semibold">Plant</th>
                  <th rowSpan="2" style={{ width: '75px' }} className="!text-sm font-semibold">Material</th>
                  <th rowSpan="2" style={{ width: '120px' }} className="!text-sm font-semibold">Description</th>
                  
                  {/* Housekeeping Group */}
                  <th colSpan="5" className="bg-primary-500/20 border-x-2 border-primary-700 font-bold !text-base text-center">
                    Housekeeping
                  </th>
                  
                  {/* Lead Times Group */}
                  <th colSpan="3" className="bg-primary-600/20 border-x-2 border-primary-700 font-bold !text-base text-center">
                    Lead Times
                  </th>
                  
                  {/* Lot Size Group */}
                  <th colSpan="5" className="bg-primary-500/20 border-x-2 border-primary-700 font-bold !text-base text-center">
                    Lot Size
                  </th>
                  
                  {/* Inventory Group */}
                  <th colSpan="5" className="bg-primary-600/20 border-x-2 border-primary-700 font-bold !text-base text-center">
                    Inventory
                  </th>
                  
                  {/* Item is Reviewed Group */}
                  <th style={{ width: '90px' }} className="bg-primary-500/20 border-x-2 border-primary-700 font-bold !text-base text-center">
                    Item is Reviewed
                  </th>
                  
                  <th rowSpan="2" style={{ width: '90px' }} className="!text-sm font-semibold">Assigned To</th>
                  <th rowSpan="2" style={{ width: '90px' }} className="!text-sm font-semibold">Due Date</th>
                </tr>
                
                {/* Field Names Row */}
                <tr>
                  {/* Housekeeping Fields */}
                  <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px' }} className="bg-primary-500/20 border-l-2 border-primary-700">MRP Type</th>
                  <th style={{ width: '100px' }} className="bg-primary-500/20">Is Inactivation Recommended</th>
                  <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px' }} className="bg-primary-500/20">Special Procurement Key</th>
                  <th style={{ width: '100px', paddingLeft: '12px' }} className="bg-primary-500/20">Is MRP Controller Inaccurate</th>
                  <th style={{ width: '120px' }} className="bg-primary-500/20 border-r-2 border-primary-700">Purchasing Group</th>
                  
                  {/* Lead Times Fields */}
                  <th style={{ width: '100px' }} className="bg-primary-600/20">Planning Time Fence</th>
                  <th style={{ width: '110px' }} className="bg-primary-600/20">Planned Delivery Time</th>
                  <th style={{ width: '130px' }} className="bg-primary-600/20 border-r-2 border-primary-700">Goods Receipt Processing Time</th>
                  
                  {/* Lot Size Fields */}
                  <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px' }} className="bg-primary-500/20">Lot Size</th>
                  <th style={{ width: '110px' }} className="bg-primary-500/20">Minimum Lot Size</th>
                  <th style={{ width: '100px' }} className="bg-primary-500/20">Fixed Lot Size</th>
                  <th style={{ width: '110px' }} className="bg-primary-500/20">Maximum Lot Size</th>
                  <th style={{ width: '100px' }} className="bg-primary-500/20 border-r-2 border-primary-700">Rounding Value</th>
                  
                  {/* Inventory Fields */}
                  <th style={{ width: '70px', minWidth: '70px', maxWidth: '70px' }} className="bg-primary-600/20">Strategy Group</th>
                  <th style={{ width: '110px' }} className="bg-primary-600/20">Absolute Minimum</th>
                  <th style={{ width: '100px' }} className="bg-primary-600/20">Safety Stock</th>
                  <th style={{ width: '110px' }} className="bg-primary-600/20">Maximum Stock Level</th>
                  <th style={{ width: '110px' }} className="bg-primary-600/20 border-r-2 border-primary-700">Target Inventory</th>
                  
                  {/* Item is Reviewed Field */}
                  <th className="bg-primary-500/20 border-l-2 border-r-2 border-primary-700">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        onChange={toggleAllReviewStatus}
                        disabled={showReviewed}
                        checked={records.length > 0 && records.every(record => {
                          const isReviewed = pendingReviewChanges[record.MaterialPlantRecordID] !== undefined
                            ? pendingReviewChanges[record.MaterialPlantRecordID]
                            : (record.IsRecordReviewed === 1 || record.IsRecordReviewed === true);
                          return isReviewed;
                        })}
                        title={showReviewed ? "Cannot modify reviewed records" : "Check/Uncheck all"}
                        className="cursor-pointer"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => {
                  const hasEdits = pendingEdits[record.MaterialPlantRecordID];
                  const hasReviewChange = pendingReviewChanges[record.MaterialPlantRecordID] !== undefined;
                  const isExpanded = expandedRows.has(record.MaterialPlantRecordID);
                  const isInDragRange = dragRange && index >= dragRange.start && index <= dragRange.end;
                  
                  // Determine if this record is marked as reviewed (considering pending changes)
                  const isReviewed = pendingReviewChanges[record.MaterialPlantRecordID] !== undefined
                    ? pendingReviewChanges[record.MaterialPlantRecordID]
                    : (record.IsRecordReviewed === 1 || record.IsRecordReviewed === true);
                  
                  return (
                    <React.Fragment key={record.MaterialPlantRecordID}>
                    <tr 
                      className={`${
                        hasEdits || hasReviewChange ? 'edited' : ''
                      } ${
                        isInDragRange ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`} 
                      data-fill-row={index}
                      style={isInDragRange ? { backgroundColor: 'rgba(59, 130, 246, 0.1)' } : {}}
                    >
                      {showReviewed && (
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedRecords.has(record.MaterialPlantRecordID)}
                            onChange={() => toggleSelectRecord(record.MaterialPlantRecordID)}
                            className="cursor-pointer"
                            title="Select for unmarking"
                          />
                        </td>
                      )}
                      <td>
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedRows);
                            if (isExpanded) {
                              newExpanded.delete(record.MaterialPlantRecordID);
                            } else {
                              newExpanded.add(record.MaterialPlantRecordID);
                            }
                            setExpandedRows(newExpanded);
                          }}
                          className="text-gray-500 hover:text-gray-700 focus:outline-none text-sm font-bold"
                          title={isExpanded ? "Collapse details" : "Expand details"}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </td>
                      <td>{record.Plant}</td>
                      <td>{record.Material ? record.Material.replace(/^0+/, '') : ''}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{record.MaterialDescription}</td>
                      
                      {/* Housekeeping Group Fields */}
                      {/* 1. MRP Type - Dropdown */}
                      <td className="bg-primary-500/10 border-l-2 border-primary-700">
                        <DraggableFillCell
                          value={record.MRPType}
                          type="select"
                          options={lookupTables.mrp_types || []}
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'MRPType', value)}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="MRPType"
                        />
                      </td>
                      
                      {/* 2. Is Inactivation Recommended - Checkbox */}
                      <td className="text-center bg-primary-500/10">
                        <input
                          type="checkbox"
                          checked={record.IsInactivationRecommended || false}
                          onChange={(e) => handleCellEdit(record.MaterialPlantRecordID, 'IsInactivationRecommended', e.target.checked)}
                          disabled={showReviewed}
                        />
                      </td>
                      
                      {/* 3. Special Procurement Key - Dropdown */}
                      <td className="bg-primary-500/10">
                        <DraggableFillCell
                          value={record.SpecialProcurementKey}
                          type="select"
                          options={getSpecialProcurementKeysForPlant(record.Plant)}
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'SpecialProcurementKey', value)}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="SpecialProcurementKey"
                        />
                      </td>
                      
                      {/* 4. Is MRP Controller Inaccurate - Checkbox */}
                      <td className="text-center bg-primary-500/10" style={{ paddingLeft: '12px' }}>
                        <input
                          type="checkbox"
                          checked={record.IsMRPControllerInaccurate || false}
                          onChange={(e) => handleCellEdit(record.MaterialPlantRecordID, 'IsMRPControllerInaccurate', e.target.checked)}
                          disabled={showReviewed}
                        />
                      </td>
                      
                      {/* 5. Purchasing Group - Dropdown */}
                      <td className="bg-primary-500/10 border-r-2 border-primary-700">
                        <DraggableFillCell
                          value={record.PurchasingGroup}
                          type="select"
                          options={lookupTables.purchasing_groups || []}
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'PurchasingGroup', value)}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="PurchasingGroup"
                        />
                      </td>
                      
                      {/* Lead Times Group Fields */}
                      {/* 6. Planning Time Fence - Free text */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={formatDecimal(record.PlanningTimeFence)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'PlanningTimeFence', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="PlanningTimeFence"
                        />
                      </td>
                      
                      {/* 7. Planned Delivery Time - Free text */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={formatDecimal(record.PlannedDeliveryTime)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'PlannedDeliveryTime', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="PlannedDeliveryTime"
                        />
                      </td>
                      
                      {/* 8. Goods Receipt Processing Time - Free text */}
                      <td className="bg-primary-600/10 border-r-2 border-primary-700">
                        <DraggableFillCell
                          value={formatDecimal(record.GoodsReceiptProcessingTime)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'GoodsReceiptProcessingTime', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="GoodsReceiptProcessingTime"
                        />
                      </td>
                      
                      {/* Lot Size Group Fields */}
                      {/* 9. Lot Size - Dropdown */}
                      <td className="bg-primary-500/10">
                        <DraggableFillCell
                          value={record.LotSize}
                          type="select"
                          options={lookupTables.lot_sizes || []}
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'LotSize', value)}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="LotSize"
                        />
                      </td>
                      
                      {/* 10. Minimum Lot Size */}
                      <td className="bg-primary-500/10">
                        <DraggableFillCell
                          value={formatDecimal(record.MinimumLotSize)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'MinimumLotSize', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="MinimumLotSize"
                        />
                      </td>
                      
                      {/* 11. Fixed Lot Size - Free text */}
                      <td className="bg-primary-500/10">
                        <DraggableFillCell
                          value={formatDecimal(record.FixedLotSize)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'FixedLotSize', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="FixedLotSize"
                        />
                      </td>
                      
                      {/* 12. Maximum Lot Size - Free text */}
                      <td className="bg-primary-500/10">
                        <DraggableFillCell
                          value={formatDecimal(record.MaximumLotSize)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'MaximumLotSize', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="MaximumLotSize"
                        />
                      </td>
                      
                      {/* 13. Rounding Value - Free text */}
                      <td className="bg-primary-500/10 border-r-2 border-primary-700">
                        <DraggableFillCell
                          value={formatDecimal(record.RoundingValue)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'RoundingValue', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="RoundingValue"
                        />
                      </td>
                      
                      {/* Inventory Group Fields */}
                      {/* 14. Strategy Group - Dropdown */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={record.StrategyGroup}
                          type="select"
                          options={lookupTables.strategy_groups || []}
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'StrategyGroup', value)}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="StrategyGroup"
                        />
                      </td>
                      
                      {/* 15. Absolute Minimum - Free text */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={formatDecimal(record.AbsoluteMinimum)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'AbsoluteMinimum', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="AbsoluteMinimum"
                        />
                      </td>
                      
                      {/* 16. Safety Stock - Free text */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={formatDecimal(record.SafetyStock)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'SafetyStock', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="SafetyStock"
                        />
                      </td>
                      
                      {/* 17. Maximum Stock Level - Free text */}
                      <td className="bg-primary-600/10">
                        <DraggableFillCell
                          value={formatDecimal(record.MaximumStockLevel)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'MaximumStockLevel', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="MaximumStockLevel"
                        />
                      </td>
                      
                      {/* 18. Target Inventory - Free text */}
                      <td className="bg-primary-600/10 border-r-2 border-primary-700">
                        <DraggableFillCell
                          value={formatDecimal(record.TargetInventory)}
                          type="number"
                          onChange={(value) => handleCellEdit(record.MaterialPlantRecordID, 'TargetInventory', parseFloat(value))}
                          onFillDown={handleFillDown}
          onDragRangeChange={handleDragRangeChange}
                          disabled={showReviewed}
                          recordIndex={index}
                          totalRecords={records.length}
                          field="TargetInventory"
                        />
                      </td>
                      
                      {/* Item is Reviewed - Checkbox */}
                      <td className="text-center bg-primary-500/10 border-x-2 border-primary-700">
                        <input
                          type="checkbox"
                          checked={isReviewed}
                          onChange={() => toggleReviewStatus(record.MaterialPlantRecordID, isReviewed)}
                          disabled={showReviewed}
                          title={isReviewed ? "Unmark as reviewed" : "Mark as reviewed"}
                        />
                      </td>
                      
                      <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{record.AssignedToPlannerName}</td>
                      <td>{formatShortDate(record.RecordReviewDueDate)}</td>
                    </tr>
                    
                    {/* Expandable Detail Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td colSpan={showReviewed ? "28" : "27"} className="p-3">
                          <div className="grid grid-cols-7 gap-4 text-xs">
                            <div className="col-span-2">
                              <span className="font-semibold text-gray-700">Family:</span>
                              <div className="text-gray-900 mt-0.5">
                                {record.Family} - {record.FamilyDescription}
                              </div>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Current Inventory:</span>
                              <div className="text-gray-900 mt-0.5">
                                {formatNumber(record.CurrentInventory)} {record.UnitOfMeasure || ''}
                              </div>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Goods Movement (Past 12 Months):</span>
                              <div className="text-gray-900 mt-0.5">
                                {formatNumber(record.GoodsMovementPast12Months)} {record.UnitOfMeasure || ''}
                              </div>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Procurement Type:</span>
                              <div className="text-gray-900 mt-0.5">{record.ProcurementType || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">MRP Controller:</span>
                              <div className="text-gray-900 mt-0.5">
                                {record.MRPControllerID} - {record.MRPControllerDescription}
                              </div>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-700">Material Create Date:</span>
                              <div className="text-gray-900 mt-0.5">{formatShortDate(record.MaterialCreateDate)}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            totalItems={totalItems}
          />
        </>
      )}
      
      {/* Success Confirmation Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
        }}
        title={successDetails.action === 'saved' ? 'Changes Saved Successfully!' : 'Records Marked as Reviewed!'}
        size="lg"
      >
        <div className="py-4">
          <p className="text-gray-700 mb-4">
            {successDetails.action === 'saved' 
              ? `Successfully updated ${successDetails.records.length} record(s) in the database.`
              : `Successfully marked ${successDetails.records.length} record(s) as reviewed.`
            }
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Plant</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Material</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Description</th>
                  {successDetails.action === 'saved' && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Changes Made</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {successDetails.records.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{record.plant}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{record.material}</td>
                    <td className="px-3 py-2">{record.description}</td>
                    {successDetails.action === 'saved' && (
                      <td className="px-3 py-2">
                        <div className="text-xs space-y-1">
                          {record.changes && Object.entries(record.changes)
                            .filter(([key]) => key !== 'MaterialPlantRecordID')
                            .map(([field, value]) => (
                              <div key={field}>
                                <span className="font-semibold">{field}:</span> {value?.toString() || 'cleared'}
                              </div>
                            ))
                          }
                          {record.reviewStatus && (
                            <div>
                              <span className="font-semibold">Review Status:</span> {record.reviewStatus}
                            </div>
                          )}
                          {!record.changes && !record.reviewStatus && (
                            <div className="text-gray-500">No changes</div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => {
              setShowSuccessModal(false);
            }}
            className="btn btn-primary"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MaterialReview;


